import 'intersection-observer';

import { CustomAppContext } from '../components/app/types';

import React, { useEffect } from 'react';
import App, { AppProps } from 'next/app';
import { OptimizelyProvider } from '@optimizely/react-sdk';
import dynamic from 'next/dynamic';
import Router from 'next/router';
import cookies from 'next-cookies';

import { wrapper } from '../store';
import useAnalytics from '../shared/hooks/app/useAnalytics';
import { useApollo } from 'shared/graphql/apolloClient';
import { ApolloProvider } from '@apollo/client';
import { handleNhLsrpRouting } from '../shared/helpers';
import { getAnonId } from 'shared/helpers/segment/getAnonId';

import Header from 'shared/components/Header';
import Footer from 'shared/components/Footer';
import ConsentBanner from 'shared/components/ConsentBanner';

import {
    getOptimizelyUserInfo,
    OptimizelyDataFile
} from 'shared/optimizely/optimizely';
import { getOptimizelyReactSDKClient } from 'shared/optimizely/optimizelyClientInstance';
import {
    forceExperiments,
    getOptimizelyOverrides,
    OptimizelyOverrides,
    writeOverridesToCookies
} from 'shared/optimizely/optimizelyOverrides';
import { setForcedFeatures } from 'reducers/optimizely';
import { useFetchUser } from 'shared/hooks/useFetchUser';

import { ThemeProvider } from '@blueprint/ui';
import { zooplaTheme } from '@blueprint/theme';
import { CacheProvider } from '@emotion/react';
import { cache } from '@emotion/css';

const MainScript = dynamic(() => import('shared/components/main'), {
    ssr: false
});

type PageProps = {
    userAgent?: string;
    optimizelyDatafile?: OptimizelyDataFile;
    initialApolloState: Record<string, unknown>;
    optimizelyOverrides?: OptimizelyOverrides;
    anonId: string;
};

interface InitialisedAppProps extends AppProps<PageProps> {
    pageProps: PageProps;
}

const halfASecInMs = 500;

function AppWrapper({ Component, pageProps }: InitialisedAppProps) {
    const {
        optimizelyDatafile,
        optimizelyOverrides,
        userAgent,
        anonId
    } = pageProps;

    useAnalytics();
    useFetchUser();

    useEffect(() => {
        if (document) {
            document.documentElement.className = 'js';
        }
    }, []);
    const client = useApollo(pageProps.initialApolloState);

    useEffect(() => {
        Router.events.on('routeChangeStart', handleNhLsrpRouting);

        return () => {
            Router.events.off('routeChangeComplete', handleNhLsrpRouting);
        };
    }, [Router.events]);

    useEffect(() => {
        if (optimizelyOverrides) {
            writeOverridesToCookies(optimizelyOverrides);
        }
    }, [optimizelyOverrides]);

    const optimizely = getOptimizelyReactSDKClient(optimizelyDatafile);

    forceExperiments(optimizelyOverrides?.experiments, anonId, optimizely);

    const optimizelyUserInfo = getOptimizelyUserInfo(anonId, userAgent);

    return (
        <OptimizelyProvider
            optimizely={optimizely}
            isServerSide={typeof window === 'undefined'}
            user={optimizelyUserInfo}
            timeout={halfASecInMs}
        >
            <CacheProvider value={cache}>
                <ThemeProvider theme={zooplaTheme}>
                    <ApolloProvider client={client}>
                        <Header />
                        <ConsentBanner />
                        <MainScript />
                        <Component {...pageProps} />
                        <Footer />
                    </ApolloProvider>
                </ThemeProvider>
            </CacheProvider>
        </OptimizelyProvider>
    );
}

// Required in order to make all pages non-statically optimised since we need
// to fetch per-user data in _document.js
AppWrapper.getInitialProps = async (appContext: CustomAppContext) => {
    const appProps = await App.getInitialProps(appContext);
    const pageProps: PageProps = appProps.pageProps;
    const allCookies = cookies(appContext.ctx, { doNotParse: true });

    const anonId = getAnonId(allCookies);

    const optimizelyOverrides = getOptimizelyOverrides(
        allCookies,
        appContext.ctx.query
    );

    appContext.ctx.store.dispatch(
        setForcedFeatures(optimizelyOverrides.features)
    );

    const userAgent =
        typeof window === 'undefined'
            ? appContext.ctx.req?.headers['user-agent']
            : window.navigator.userAgent;

    pageProps.optimizelyDatafile =
        appContext.ctx.res?.locals?.OPTIMIZELY_DATAFILE;
    pageProps.optimizelyOverrides = optimizelyOverrides;
    pageProps.anonId = anonId;
    pageProps.userAgent = userAgent;

    if (appContext.ctx.req) {
        appContext.ctx.req.anonId = anonId;
    }

    return { ...appProps };
};

const appWithRedux = wrapper.withRedux(AppWrapper);

export default appWithRedux;
